import { type Notebook } from "@istari-frontend/notebook/model";

import { toIpynb } from "~/notebook/core/ipynb";

const NOTEBOOK_EXTENSION_PATTERN = /\.ipynb$/i;

/** Strips a trailing `.ipynb` (any casing) so a name can be reused as a display name. */
export function notebookBaseName(name: string): string {
  return name.replace(NOTEBOOK_EXTENSION_PATTERN, "");
}

/** Normalizes a name to a `.ipynb` filename, whether or not it already carries the extension. */
export function notebookFileName(name: string): string {
  return `${notebookBaseName(name)}.ipynb`;
}

/**
 * The uniform SDK setup cell every environment shares. Only *who sets the two env vars* differs
 * per platform (the kernel injects them from the browser session; elsewhere the user exports them)
 * — see the markdown cell in `blankNotebookFile`. Keeping it a plain code cell is what makes
 * platform-authored notebooks runnable in Jupyter/Colab/CI unchanged.
 */
export const SETUP_CELL_SOURCE = `import os
from istari_digital_client import Configuration, Istari

istari = Istari(Configuration(
    registry_url=os.environ["ISTARI_REGISTRY_URL"],
    registry_auth_token=os.environ["ISTARI_REGISTRY_AUTH_TOKEN"],
    filesystem_cache_enabled=False,
))`;

export function blankNotebookFile(displayName: string): File {
  const fileName = notebookFileName(displayName);
  const baseName = notebookBaseName(fileName);
  const blank: Notebook = {
    resourceId: "",
    displayName: fileName,
    extension: "ipynb",
    revisionLabel: "v1",
    cells: [
      {
        id: `cell-${crypto.randomUUID()}`,
        kind: "markdown",
        source: introMarkdown(baseName),
        links: [],
      },
      {
        id: `cell-${crypto.randomUUID()}`,
        kind: "code",
        source: SETUP_CELL_SOURCE,
        links: [],
      },
    ],
    createdAt: new Date().toISOString(),
  };
  const json = JSON.stringify(toIpynb(blank), null, 1);
  return new File([json], fileName, { type: "application/x-ipynb+json" });
}

function introMarkdown(baseName: string): string {
  return [
    `# ${baseName}`,
    "",
    "Cells run Python in your browser. The setup cell below creates the `istari` SDK client. It reads two",
    "environment variables — `ISTARI_REGISTRY_URL` and `ISTARI_REGISTRY_AUTH_TOKEN` — the same contract",
    "everywhere; only *who sets them* differs:",
    "",
    "- **Istari platform (this editor):** set automatically from your browser session — just run the cell.",
    "- **Local Jupyter / VS Code:** export both variables (the token is a personal access token), or use a `.env` file loaded with `python-dotenv`.",
    '- **Google Colab:** store both as Colab secrets, then `import os; from google.colab import userdata; os.environ["ISTARI_REGISTRY_URL"] = userdata.get("ISTARI_REGISTRY_URL")` (same for the token).',
    "- **CI:** map pipeline secrets to the two environment variables.",
    "",
    "Outside the platform, install the SDK first: `pip install istari-digital-client`.",
    "In the platform, re-run the setup cell if SDK calls start failing with expired-session (401) errors — it picks up a fresh token.",
    "",
    "```python",
    "# list resources, then read one",
    "for r in istari.resources.list(size=5).items:",
    "    print(r.resource_id)",
    "",
    'doc = istari.resources.get("<resource_id>")',
    "print(doc.read_text())  # or .read_bytes() / .read_json()",
    "```",
    "",
    "**Link a resource:** use the **+ Add** menu on a cell to insert a line binding a variable via `istari.resources.get(...)`, so you can call `.read_text()` on it directly.",
    "",
    "**Tip:** press ⌘G in a code cell to generate Python from a description.",
  ].join("\n");
}
